/**
 * BottomNav — the native-app tab bar that owns the bottom of a phone screen.
 * @file src/web/shared/components/BottomNav.tsx
 *
 * @llm-rule WHEN: The viewport is below `md`. This is THE mobile navigation.
 * @llm-rule NOTE: Replaces the hamburger dropdown. A menu behind one button is
 *   a website convention — on a phone every app puts its destinations on a
 *   fixed bottom bar, one tap away and reachable by a thumb. Nothing about the
 *   app should require reaching the top of the screen to move around.
 * @llm-rule NOTE: The bar sits ABOVE the home indicator via
 *   `env(safe-area-inset-bottom)`. Without it the last row of labels sits under
 *   the gesture bar on a notched phone and taps land on the OS, not the app.
 *   This requires `viewport-fit=cover` in index.html — the inset reads as 0px
 *   without it.
 * @llm-rule AVOID: Hover-only affordances or dropdowns that open upward off
 *   screen. "More" opens a bottom sheet anchored to the bar.
 */

import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { LayoutGrid, X, type LucideIcon } from 'lucide-react';
import type { ManageItem } from './ManageMenu';

export interface BottomTab {
  label: string;
  href: string;
  icon: LucideIcon;
  /**
   * A number worth acting on, drawn on the icon.
   *
   * Zero and undefined both render nothing — a badge showing "0" is noise that
   * trains people to ignore the badge that matters.
   */
  badge?: number;
  /** Colour the badge as a problem rather than a count. */
  badgeAlert?: boolean;
}

/**
 * Height of the bar itself, excluding the safe-area inset.
 * Exported so `AppShell` can reserve exactly this much space at the foot of the
 * page — a fixed bar is out of flow, so without the matching padding the last
 * card on every screen sits permanently underneath it.
 */
export const BOTTOM_NAV_HEIGHT = 58;

/** Reserve bar + home indicator. Used for the page's bottom padding. */
export const BOTTOM_NAV_SPACER = `calc(${BOTTOM_NAV_HEIGHT}px + env(safe-area-inset-bottom, 0px))`;

export function BottomNav({
  tabs,
  manageItems,
  currentPath,
}: {
  tabs: BottomTab[];
  /** Leadership tools. Empty for an Employee, which removes the More tab. */
  manageItems: ManageItem[];
  currentPath: string;
}) {
  const [sheetOpen, setSheetOpen] = useState(false);
  const sheetRef = useRef<HTMLDivElement>(null);

  const showMore = manageItems.length > 0;
  const moreActive = manageItems.some((item) => currentPath.startsWith(item.href));

  // Navigating closes the sheet.
  useEffect(() => {
    setSheetOpen(false);
  }, [currentPath]);

  useEffect(() => {
    if (!sheetOpen) return;

    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setSheetOpen(false);
    }
    document.addEventListener('keydown', onKey);

    // The page must not scroll behind an open sheet — on a phone that reads as
    // the sheet being stuck to a moving background.
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [sheetOpen]);

  return (
    <>
      {sheetOpen && (
        <>
          <div
            className="fixed inset-0 z-50 bg-ink/30 backdrop-blur-[2px] md:hidden"
            aria-hidden
            onClick={() => setSheetOpen(false)}
          />

          <div
            ref={sheetRef}
            role="dialog"
            aria-label="Manage"
            className="anim-sheet fixed inset-x-0 bottom-0 z-50 max-h-[76vh] overflow-y-auto rounded-t-3xl border-t border-rule bg-paper md:hidden"
            style={{
              boxShadow: 'var(--elev-3)',
              paddingBottom: `calc(${BOTTOM_NAV_SPACER} + 12px)`,
            }}
          >
            {/* The grab handle every sheet on a phone has. Purely a signifier
                that this panel came up from the bottom and goes back down. */}
            <div className="sticky top-0 bg-paper pt-2.5">
              <div className="mx-auto h-1 w-9 rounded-full bg-rule" aria-hidden />
              <div className="flex items-center justify-between px-5 pb-2 pt-3">
                <span className="text-[15px] font-semibold tracking-tight text-ink">Manage</span>
                <button
                  type="button"
                  onClick={() => setSheetOpen(false)}
                  aria-label="Close"
                  className="grid size-8 place-items-center rounded-full bg-paper-sunk text-ink-faint active:scale-95"
                >
                  <X className="size-[17px]" strokeWidth={2} />
                </button>
              </div>
            </div>

            <div className="px-3 pb-2">
              {manageItems.map((item) => {
                const active = currentPath.startsWith(item.href);
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    onClick={() => setSheetOpen(false)}
                    className={`flex items-start gap-3 rounded-2xl px-3 py-3 transition-colors active:scale-[0.99] ${
                      active ? 'bg-paper-sunk' : ''
                    }`}
                  >
                    <span className="grid size-9 flex-none place-items-center rounded-xl bg-paper-sunk">
                      <item.icon className="size-[18px] text-ink-soft" strokeWidth={1.8} />
                    </span>
                    <span className="min-w-0 pt-0.5">
                      <span className="block text-[14.5px] font-medium text-ink">{item.label}</span>
                      <span className="block text-[11.5px] leading-snug text-ink-faint">
                        {item.hint}
                      </span>
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/*
        NOT `aria-label="Main"` — the header nav already owns that name. Two
        landmarks with one label is a screen reader announcing "Main navigation"
        twice with no way to tell them apart, and only one of the two is ever
        visible at a given width.
      */}
      <nav
        aria-label="Primary"
        className="fixed inset-x-0 bottom-0 z-40 border-t border-rule bg-paper/90 backdrop-blur-xl backdrop-saturate-150 md:hidden"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        <div className="flex items-stretch">
          {tabs.map((tab) => {
            const active = currentPath.startsWith(tab.href);
            return (
              <Link
                key={tab.href}
                to={tab.href}
                aria-current={active ? 'page' : undefined}
                className="group relative flex flex-1 flex-col items-center justify-center gap-[3px] active:scale-95"
                style={{ height: BOTTOM_NAV_HEIGHT }}
              >
                <span className="relative">
                  <tab.icon
                    className={`size-[21px] transition-colors ${
                      active ? 'text-brand' : 'text-ink-faint'
                    }`}
                    strokeWidth={active ? 2.2 : 1.8}
                  />

                  {/* The count IS the way in: you read it here and tap it,
                      rather than opening the screen to find out whether
                      anything was waiting. */}
                  {Boolean(tab.badge) && (
                    <span
                      className="absolute -right-2.5 -top-1.5 grid h-[16px] min-w-[16px] place-items-center rounded-full px-1 text-[9.5px] font-bold tabular-nums text-white"
                      style={{
                        background: tab.badgeAlert ? 'var(--dot-flag)' : 'var(--brand)',
                        boxShadow: '0 0 0 2px var(--paper)',
                      }}
                    >
                      {tab.badge! > 99 ? '99+' : tab.badge}
                    </span>
                  )}
                </span>
                <span
                  className={`text-[10px] leading-none tracking-tight transition-colors ${
                    active ? 'font-semibold text-brand' : 'font-medium text-ink-faint'
                  }`}
                >
                  {tab.label}
                </span>
              </Link>
            );
          })}

          {showMore && (
            <button
              type="button"
              onClick={() => setSheetOpen((v) => !v)}
              aria-expanded={sheetOpen}
              aria-haspopup="dialog"
              className="relative flex flex-1 flex-col items-center justify-center gap-[3px] active:scale-95"
              style={{ height: BOTTOM_NAV_HEIGHT }}
            >
              <LayoutGrid
                className={`size-[21px] transition-colors ${
                  moreActive || sheetOpen ? 'text-brand' : 'text-ink-faint'
                }`}
                strokeWidth={moreActive || sheetOpen ? 2.2 : 1.8}
              />
              <span
                className={`text-[10px] leading-none tracking-tight transition-colors ${
                  moreActive || sheetOpen
                    ? 'font-semibold text-brand'
                    : 'font-medium text-ink-faint'
                }`}
              >
                More
              </span>
            </button>
          )}
        </div>
      </nav>
    </>
  );
}
